// ========================================
// Professor Service
// Handles all professor-related operations
// ========================================

const ProfessorService = {
    /**
     * Get all professors
     */
    getProfessors() {
        return DataManager.getAll(DataManager.KEYS.PROFESSORS);
    },

    /**
     * Get professor by ID
     */
    getProfessor(id) {
        return DataManager.getById(DataManager.KEYS.PROFESSORS, id);
    },

    /**
     * Generate slug from professor name
     */
    getSlug(professor) {
        if (!professor) return '';
        const name = `${professor.firstName || ''} ${professor.lastName || ''}`.trim().replace(/\s+/g, ' ');
        return Utils.toTitleCase(name).replace(/\s+/g, '_');
    },

    /**
     * Get professor by Slug (Name_Surname) or ID
     */
    getProfessorBySlug(slug) {
        if (!slug) return null;
        const decodedSlug = decodeURIComponent(slug);
        const professors = this.getProfessors();
        if (!professors || professors.length === 0) return null;

        // 1. Direct ID match
        let found = professors.find(p => String(p.id) === String(decodedSlug) || String(p.id) === String(slug));
        if (found) return found;

        // 2. Direct Slug match
        const lowerSlug = decodedSlug.toLowerCase();
        found = professors.find(p => this.getSlug(p).toLowerCase() === lowerSlug);
        if (found) return found;

        // 3. Normalized Name match (handles accents, trailing/extra spaces)
        const normalizedInputSlug = Utils.normalizeString(decodedSlug).replace(/_/g, ' ').trim();
        return professors.find(p => {
            const fullProfName = `${p.firstName || ''} ${p.lastName || ''}`.trim().replace(/\s+/g, ' ');
            return Utils.normalizeString(fullProfName) === normalizedInputSlug;
        }) || null;
    },

    cacheProfessor(professor) {
        const rows = this.getProfessors();
        const existing = rows.find(p => p.id === professor.id);
        const updated = existing ? rows.map(p => p.id === professor.id ? { ...p, ...professor } : p) : [...rows, professor];
        FileStorage.dataStore.professors = updated;
        try { BrowserState.setItem(DataManager.KEYS.PROFESSORS, JSON.stringify(updated)); } catch { /* Server confirmed; memory cache remains usable. */ }
        return professor;
    },

    async persistProfessor(method, id, data) {
        const epoch = AuthManager._epoch;
        const token = AuthManager.getCsrfToken();
        if (!token) throw new Error('Inicia sesión antes de guardar la ficha.');
        // Coordination edits directory fields only; it never sends empty private fields.
        if (data && !['admin','rrhh'].includes(AuthManager.getCurrentUser()?.role) && !AuthManager.hasModule('personal-laboral','manage')) {
            data = Object.fromEntries(['firstName', 'lastName', 'specialty'].filter(k => Object.hasOwn(data, k)).map(k => [k, data[k]]));
        }
        const response = await fetch('/api/professors' + (id ? '/' + encodeURIComponent(id) : ''), {
            method, headers: { 'X-SICAF-CSRF': token, 'Content-Type': 'application/json' },
            ...(data ? { body: JSON.stringify(data) } : {})
        });
        const result = await response.json().catch(() => null);
        if (epoch !== AuthManager._epoch) throw new Error('La sesión ha cambiado. Vuelve a entrar.');
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo guardar la ficha del profesor.');
        return result;
    },

    async createProfessor(data) {
        const result = await this.persistProfessor('POST', null, data);
        return this.cacheProfessor(result.professor);
    },

    async updateProfessor(id, data) {
        const result = await this.persistProfessor('PUT', id, data);
        return this.cacheProfessor(result.professor);
    },

    /**
     * A person's contracts (each hire, with its dates and pay): create, change or remove one.
     * The server answers with the whole ficha, its contracts and the focus one mirrored.
     */
    async saveContract(method, url, data) {
        const epoch = AuthManager._epoch;
        const response = await fetch(url, { method, headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken(), 'Content-Type': 'application/json' },
            ...(data ? { body: JSON.stringify(data) } : {}) });
        const result = await response.json().catch(() => null);
        if (epoch !== AuthManager._epoch) throw new Error('La sesión ha cambiado. Vuelve a entrar.');
        if (!response.ok || !result?.success) throw new Error(result?.error || 'No se pudo guardar el contrato.');
        return this.cacheProfessor(result.professor);
    },

    createContract(professorId, data) {
        return this.saveContract('POST', '/api/professors/' + encodeURIComponent(professorId) + '/contracts', data);
    },

    updateContract(contractId, data) {
        return this.saveContract('PUT', '/api/staff-contracts/' + encodeURIComponent(contractId), data);
    },

    deleteContract(contractId) {
        return this.saveContract('DELETE', '/api/staff-contracts/' + encodeURIComponent(contractId));
    },

    async deleteProfessor(id) {
        await this.persistProfessor('DELETE', id, { confirmId: id });
        const rows = this.getProfessors().filter(p => p.id !== id);
        FileStorage.dataStore.professors = rows;
        try { BrowserState.setItem(DataManager.KEYS.PROFESSORS, JSON.stringify(rows)); } catch { /* Do not report a failed delete after server confirmation. */ }
        return true;
    },

    /**
     * Calculate total weekly hours for a professor
     */
    getProfessorWeeklyHours(professorId) {
        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.professorId === professorId);

        let totalHours = 0;
        classes.forEach(cls => {
            const duration = Utils.calculateDuration(cls.startTime, cls.endTime);
            totalHours += duration;
        });

        return totalHours;
    },

    /**
     * Calculate weekly cost for a professor
     */
    getProfessorWeeklyCost(professorId) {
        const professor = this.getProfessor(professorId);
        if (!professor) return 0;

        const hours = this.getProfessorWeeklyHours(professorId);
        return hours * StaffPay.hourlyRate(professor);
    },

    /**
     * Get professor's full name
     */
    getProfessorFullName(professorId) {
        const professor = this.getProfessor(professorId);
        if (!professor) return 'Desconocido';
        return `${professor.firstName} ${professor.lastName}`;
    },

    /**
     * Get professor statistics
     */
    getProfessorStats(professorId) {
        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.professorId === professorId);
        const subjects = DataManager.getAll(DataManager.KEYS.SUBJECTS)
            .filter(s => s.professorId === professorId);

        const weeklyHours = this.getProfessorWeeklyHours(professorId);
        const weeklyCost = this.getProfessorWeeklyCost(professorId);

        return {
            totalClasses: classes.length,
            totalSubjects: subjects.length,
            weeklyHours,
            weeklyCost
        };
    },
    /**
     * Calculate total monthly hours for a professor (exact)
     */
    getProfessorMonthlyHours(professorId, year, month) {
        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.professorId === professorId);

        if (classes.length === 0) return 0;

        let totalHours = 0;
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

            // Skip holidays
            if (DataManager.isHoliday(dateStr)) continue;

            const dayOfWeek = date.getDay();

            // Find classes for this day
            const dayClasses = classes.filter(cls => {
                if (cls.dayOfWeek !== dayOfWeek) return false;

                const start = new Date(cls.startDate);
                start.setHours(0, 0, 0, 0);
                const end = cls.endDate ? new Date(cls.endDate) : null;
                if (end) end.setHours(23, 59, 59, 999);

                if (date < start) return false;
                if (end && date > end) return false;

                return true;
            });

            dayClasses.forEach(cls => {
                totalHours += Utils.calculateDuration(cls.startTime, cls.endTime);
            });
        }

        return totalHours;
    },
    /**
     * Format a Date as a local YYYY-MM-DD string. Using toISOString() here would
     * convert to UTC first, which silently shifts the date back a day in any
     * positive-offset timezone (e.g. Europe) — a single-day class stored as
     * startDate === endDate would then never match the iterated day at all.
     */
    _localDateStr(date) {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    },

    /**
     * Calculate total hours for a professor in a specific period (exact)
     */
    getProfessorTotalHoursInPeriod(professorId, startDate, endDate) {
        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.professorId === professorId);

        if (classes.length === 0) return 0;

        let totalHours = 0;
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Iterate through each day in the period
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dateStr = this._localDateStr(d);

            // Skip holidays
            if (DataManager.isHoliday(dateStr)) continue;

            const dayOfWeek = d.getDay();

            // Find classes for this day
            const dayClasses = classes.filter(cls => {
                if (cls.dayOfWeek !== dayOfWeek) return false;

                const clsStartStr = cls.startDate.split('T')[0];
                const clsEndStr = cls.endDate ? cls.endDate.split('T')[0] : null;

                if (dateStr < clsStartStr) return false;
                if (clsEndStr && dateStr > clsEndStr) return false;

                return true;
            });

            dayClasses.forEach(cls => {
                totalHours += Utils.calculateDuration(cls.startTime, cls.endTime);
            });
        }

        return totalHours;
    },

    /**
     * Calculate average weekly hours based on contract period
     */
    getProfessorAverageWeeklyHours(professorId) {
        const professor = this.getProfessor(professorId);
        if (!professor) return 0;
        if (StaffPay.isFixed(professor)) return StaffPay.regularWeeklyHours(professor.workSchedule);

        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.professorId === professorId);

        if (classes.length === 0) return 0;

        // Determine period start and end
        let startDate = professor.hireDate ? new Date(professor.hireDate) : null;
        let endDate = professor.terminationDate ? new Date(professor.terminationDate) : null;

        // Fallback to class range if no contract dates
        if (!startDate) {
            const minDate = classes.reduce((min, c) => c.startDate < min ? c.startDate : min, classes[0].startDate);
            startDate = new Date(minDate);
        }

        if (!endDate) {
            // If no termination date, determine reasonable end (e.g., max class date or end of current academic year)
            // For now, let's use the max class date
            const maxDate = classes.reduce((max, c) => (c.endDate || c.startDate) > max ? (c.endDate || c.startDate) : max, classes[0].startDate);
            endDate = new Date(maxDate);

            // If max date is in the past, maybe use today? allow it to be max date.
            // If active indefinite, maybe use end of current academic year (June 30th)
            const today = new Date();
            const currentYear = today.getFullYear();
            const academicYearEnd = new Date(today.getMonth() >= 8 ? currentYear + 1 : currentYear, 5, 30); // June 30th

            if (endDate < academicYearEnd) {
                endDate = academicYearEnd;
            }
        }

        return this.getProfessorAverageWeeklyHoursInPeriod(professorId, startDate, endDate);
    },

    /**
     * Calculate average weekly hours within an arbitrary [startDate, endDate] period
     * (same "count real calendar weeks, skip holidays" logic as
     * getProfessorAverageWeeklyHours, factored out so callers — e.g. the professor
     * profile view scoping stats to the selected academic year — can reuse it with
     * a period other than the contract's hire/termination dates).
     */
    getProfessorAverageWeeklyHoursInPeriod(professorId, startDate, endDate) {
        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.professorId === professorId);

        if (classes.length === 0 || startDate > endDate) return 0;

        // Calculate total hours in that period
        const totalHours = this.getProfessorTotalHoursInPeriod(professorId, startDate, endDate);

        // Count unique weeks that have at least one class
        // A week is defined as Monday to Sunday
        const weeksWithClasses = new Set();

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const dateStr = this._localDateStr(d);

            // Skip holidays
            if (DataManager.isHoliday(dateStr)) continue;

            const dayOfWeek = d.getDay();

            // Check if this day has any classes
            const hasClasses = classes.some(cls => {
                if (cls.dayOfWeek !== dayOfWeek) return false;

                const clsStartStr = cls.startDate.split('T')[0];
                const clsEndStr = cls.endDate ? cls.endDate.split('T')[0] : null;

                if (dateStr < clsStartStr) return false;
                if (clsEndStr && dateStr > clsEndStr) return false;

                return true;
            });

            if (hasClasses) {
                // Get Monday of this week (ISO week)
                const tempDate = new Date(d);
                const dayOfWeek = tempDate.getDay();
                const diff = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek; // Adjust to Monday
                tempDate.setDate(tempDate.getDate() + diff);
                const weekKey = this._localDateStr(tempDate); // Monday's date as key
                weeksWithClasses.add(weekKey);
            }
        }

        if (weeksWithClasses.size === 0) return 0;

        // Convert Set to sorted array to find first and last week
        const weekKeys = Array.from(weeksWithClasses).sort();
        const firstWeek = new Date(weekKeys[0]);
        const lastWeek = new Date(weekKeys[weekKeys.length - 1]);

        // Count all weeks between first and last (inclusive), including empty weeks
        const diffTime = lastWeek - firstWeek;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        const totalWeeks = Math.max(1, Math.floor(diffDays / 7) + 1);

        return totalHours / totalWeeks;
    },

    /**
     * Calculate TOTAL hours assigned to a professor (All time)
     */
    getProfessorTotalHours(professorId) {
        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.professorId === professorId);

        let totalHours = 0;
        classes.forEach(cls => {
            totalHours += Utils.calculateDuration(cls.startTime, cls.endTime);
        });

        return totalHours;
    },

    /**
     * Calculate monthly payroll for a professor
     * Divides total cost by number of months in employment period
     */
    getProfessorMonthlyPayroll(professorId) {
        const professor = this.getProfessor(professorId);
        if (!professor) return 0;
        if (StaffPay.isFixed(professor)) return StaffPay.monthlySalary(professor);

        const classes = DataManager.getAll(DataManager.KEYS.CLASSES)
            .filter(c => c.professorId === professorId);

        if (classes.length === 0) return 0;

        // Determine period start and end
        let startDate = professor.hireDate ? new Date(professor.hireDate) : null;
        let endDate = professor.terminationDate ? new Date(professor.terminationDate) : null;

        // Fallback to class range if no contract dates
        if (!startDate) {
            const minDate = classes.reduce((min, c) => c.startDate < min ? c.startDate : min, classes[0].startDate);
            startDate = new Date(minDate);
        }

        if (!endDate) {
            const maxDate = classes.reduce((max, c) => (c.endDate || c.startDate) > max ? (c.endDate || c.startDate) : max, classes[0].startDate);
            endDate = new Date(maxDate);

            const today = new Date();
            const currentYear = today.getFullYear();
            const academicYearEnd = new Date(today.getMonth() >= 8 ? currentYear + 1 : currentYear, 5, 30);

            if (endDate < academicYearEnd) {
                endDate = academicYearEnd;
            }
        }

        // Calculate number of months (counting partial months)
        const startYear = startDate.getFullYear();
        const startMonth = startDate.getMonth();
        const endYear = endDate.getFullYear();
        const endMonth = endDate.getMonth();

        const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;

        // Calculate total cost for the period
        const totalHours = this.getProfessorTotalHoursInPeriod(professorId, startDate, endDate);
        const totalCost = totalHours * StaffPay.hourlyRate(professor);

        return totalCost / Math.max(1, totalMonths);
    },

    /**
     * Handle logic for advisory notifications
     */
    async checkDailyAdvisoryNotification(professorId) {
        const user = AuthManager.getCurrentUser();
        if (!user || !['admin', 'rrhh'].includes(user.role)) return;
        const professor = this.getProfessor(professorId);
        if (!professor || professor.advisoryNotificationChecked) return;
        this._advisoryRequests ||= new Set();
        if (this._advisoryRequests.has(professorId)) return;
        this._advisoryRequests.add(professorId);
        try {
            const response = await fetch('/api/professors/' + encodeURIComponent(professorId) + '/advisory-reminder', {
                method: 'POST', headers: { 'X-SICAF-CSRF': AuthManager.getCsrfToken() }
            });
            const result = await response.json().catch(() => null);
            if (!response.ok || !result?.success) throw new Error('No se pudo comprobar el recordatorio de asesoría');
            if (result.state) {
                // Update the cache only after the dedicated transaction has committed.
                const updated = this.getProfessors().map(p => p.id === professorId ? { ...p, ...result.state } : p);
                FileStorage.dataStore.professors = updated;
                BrowserState.setItem(DataManager.KEYS.PROFESSORS, JSON.stringify(updated));
            }
        } catch {
            if (typeof Utils !== 'undefined') Utils.showToast('No se pudo comprobar el recordatorio de asesoría. Inténtalo de nuevo.', 'error');
        } finally {
            this._advisoryRequests.delete(professorId);
        }
    },

    /**
     * Ask the server to send the «Alta de profesor/a» notice to the advisory (Ajustes de Correo).
     */
    async sendAdvisoryEmail(professorId, contractId = null, { quiet = false } = {}) {
        const professor = this.getProfessor(professorId);
        if (!professor) return false;

        // Get statistics and formatted values
        const weeklyHours = this.getProfessorAverageWeeklyHours(professorId);
        const monthlyPayroll = this.getProfessorMonthlyPayroll(professorId);

        // The email itself is the «Alta de profesor/a» template (Ajustes de Correo), sent by the server.
        try {
            const response = await fetch('/api/professors/' + encodeURIComponent(professorId) + '/advisory-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() },
                body: JSON.stringify({ weeklyHours: Number(weeklyHours) || 0, monthlyPayroll: Number(monthlyPayroll) || 0, ...(contractId ? { contractId } : {}) })
            });

            const result = await response.json();

            if (result.success && result.professor) {
                // The server marked that contract as notified.
                this.cacheProfessor(result.professor);
                if (!quiet) Utils.showToast(`Aviso enviado a la asesoría (${result.to})`, 'success');
                return true;
            } else if (result.success) {
                try { await this.updateProfessor(professorId, {
                    advisoryNotificationChecked: true,
                    advisoryNotificationDate: new Date().toISOString()
                }); } catch {
                    Utils.showToast('El correo se ha enviado, pero no se pudo guardar su estado. No lo reenvíes sin comprobarlo.', 'warning');
                    return false;
                }
                if (!quiet) Utils.showToast(`Aviso enviado a la asesoría (${result.to})`, 'success');
                return true;
            } else {
                throw new Error(result.error || 'Error desconocido');
            }
        } catch (error) {
            console.error('❌ Error sending advisory email:', error);
            if (!quiet) Utils.showToast(`Error al enviar email: ${error.message}`, 'danger');
            return false;
        }
    }

};

// Make ProfessorService globally available
window.ProfessorService = ProfessorService;
